// src/components/OnboardingTour.js
import React from "react";
import Joyride, { STATUS, ACTIONS, EVENTS } from "react-joyride";
import { useTranslation } from "react-i18next";
import { Button, Box, Typography } from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { userHasActiveEntitlement } from "../utils/entitlements";

export default function OnboardingTour({ isOpen, open, onClose, onFinish, stepIndex }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth() || {};
  const run = Boolean(isOpen ?? open);
  const isExternallyControlled = Number.isInteger(stepIndex) && stepIndex >= 0;
  const [internalStepIndex, setInternalStepIndex] = React.useState(0);
  const resolvedStepIndex = isExternallyControlled ? stepIndex : internalStepIndex;
  const isPremium = Boolean(userHasActiveEntitlement(user));

  const match = String(location?.pathname || "").match(/^\/([a-z]{2})\b/);
  const lang = match ? match[1] : "el";

  const goPremium = React.useCallback(() => {
    if (typeof onClose === "function") onClose();
    navigate(`/${lang}/subscription?intent=premium&plan=monthly`);
  }, [lang, navigate, onClose]);

  const steps = React.useMemo(
    () => [
      {
        target: "body",
        placement: "center",
        disableBeacon: true,
        content: (
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>
              {t("onboarding_welcome_title") || "Quick tour"}
            </Typography>
            <Typography variant="body2">
              {t("onboarding_welcome_body") || "We will highlight key features for your account."}
            </Typography>
          </Box>
        ),
      },
      {
        target: "#dashboard-premium-widget",
        placement: "bottom",
        content: isPremium
          ? (t("onboarding_premium_active") || "Premium active. Your dashboard includes premium controls.")
          : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("onboarding_unlock_premium_hint") || "Unlock Premium features from here."}
              </Typography>
              <Button variant="contained" size="small" onClick={goPremium}>
                {t("unlock_premium") || "Unlock Premium"}
              </Button>
            </Box>
          ),
      },
      {
        target: "#dashboard-meal-plan-entry",
        placement: "bottom",
        content: t("onboarding_meal_plan_entry") || "This is your meal plan entry point.",
      },
      {
        target: "#subscription-status-card",
        placement: "top",
        content: isPremium
          ? (t("onboarding_subscription_status_premium") || "Your Premium status and renewal are shown here.")
          : (
            <Box>
              <Typography variant="body2" sx={{ mb: 1 }}>
                {t("onboarding_subscription_status_free") || "Check current plan and upgrade options here."}
              </Typography>
              <Button variant="outlined" size="small" onClick={goPremium}>
                {t("unlock_premium") || "Unlock Premium"}
              </Button>
            </Box>
          ),
      },
    ],
    [goPremium, isPremium, t]
  );

  const availableSteps = React.useMemo(() => {
    return steps.filter((step) => {
      const target = String(step?.target || "").trim();
      if (!target || target === "body") return true;
      if (typeof document === "undefined") return false;
      try {
        return Boolean(document.querySelector(target));
      } catch {
        return false;
      }
    });
  }, [steps, location.pathname]);

  React.useEffect(() => {
    if (run && !isExternallyControlled) {
      setInternalStepIndex(0);
    }
  }, [run, isExternallyControlled]);

  React.useEffect(() => {
    if (!run || isExternallyControlled) return;
    if (resolvedStepIndex < availableSteps.length) return;
    setInternalStepIndex(0);
  }, [availableSteps.length, isExternallyControlled, resolvedStepIndex, run]);

  const handleCallback = (data) => {
    const finishedStatuses = [STATUS.FINISHED, STATUS.SKIPPED];
    if (finishedStatuses.includes(data.status)) {
      if (typeof onClose === "function") onClose();
      if (typeof onFinish === "function") onFinish(data);
      return;
    }

    if (!isExternallyControlled && (data.type === EVENTS.STEP_AFTER || data.type === EVENTS.TARGET_NOT_FOUND)) {
      const delta = data.action === ACTIONS.PREV ? -1 : 1;
      const nextIndex = Math.max(0, Number(data.index || 0) + delta);
      if (nextIndex >= availableSteps.length) {
        if (typeof onClose === "function") onClose();
        if (typeof onFinish === "function") onFinish(data);
        return;
      }
      setInternalStepIndex(nextIndex);
    }
  };

  if (!run || availableSteps.length === 0) {
    return null;
  }

  return (
    <Joyride
      steps={availableSteps}
      run={run}
      stepIndex={resolvedStepIndex}
      showProgress
      continuous
      showSkipButton
      disableScrolling
      locale={{
        back: t("onboarding_back"),
        close: t("onboarding_close"),
        last: t("onboarding_last"),
        next: t("onboarding_next"),
        skip: t("onboarding_skip"),
      }}
      callback={handleCallback}
      styles={{
        options: {
          primaryColor: "#49bc90",
          zIndex: 2000,
        },
      }}
    />
  );
}
